import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatMileage } from "@/lib/api";
import { BRAND, LOGO_URL } from "@/lib/brand";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function WindowSticker() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Vehicle not found. <Link to="/admin" className="ml-2 underline">Back to admin</Link>
      </div>
    );
  }
  if (!car) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  const specs = [
    ["VIN", car.vin || "—"],
    ["Mileage", formatMileage(car.mileage)],
    ["Body Style", car.body_type || "—"],
    ["Transmission", car.transmission || "—"],
    ["Fuel Type", car.fuel_type || "—"],
    ["Drivetrain", car.drivetrain || "—"],
    ["Exterior Color", car.exterior_color || "—"],
    ["Interior Color", car.interior_color || "—"],
  ];

  return (
    <div className="min-h-screen bg-secondary/60 print:bg-white" data-testid="window-sticker-page">
      <style>{`
        @page { size: letter; margin: 0.4in; }
        @media print {
          .no-print { display: none !important; }
          .sticker-sheet { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; border: none !important; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-primary text-primary-foreground px-6 py-3 flex items-center justify-between">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm hover:underline" data-testid="sticker-back">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <Button onClick={() => window.print()} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm gap-2" data-testid="sticker-print-btn">
          <Printer className="h-4 w-4" /> Print Sticker
        </Button>
      </div>

      <div className="sticker-sheet bg-white text-black max-w-[8.5in] mx-auto my-8 shadow-xl border border-black/10 print:my-0">
        {/* HEADER */}
        <div className="bg-black text-white px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt={BRAND.name} className="h-16 w-auto" />
            <div>
              <p className="font-serif text-3xl leading-none tracking-wide uppercase">{BRAND.name}</p>
              <p className="text-xs tracking-[0.3em] uppercase mt-1" style={{ color: "#c9a227" }}>
                Used Cars · Nampa, Idaho · {BRAND.dealerNumber}
              </p>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold text-lg">{BRAND.phone}</p>
            <p className="text-white/70">{BRAND.domain}</p>
          </div>
        </div>

        {/* VEHICLE + PRICE */}
        <div className="px-8 pt-6 pb-4 flex items-start justify-between gap-6 border-b-4" style={{ borderColor: "#631724" }}>
          <div>
            <h1 className="font-serif text-4xl leading-tight" data-testid="sticker-title">
              {car.year} {car.make} {car.model}
            </h1>
            <p className="mt-1 text-sm uppercase tracking-widest text-black/60">
              {car.body_type || "Vehicle"} · {formatMileage(car.mileage)}
            </p>
          </div>
          <div className="text-right shrink-0 text-center">
            <img
              src={`${API_BASE}/api/cars/${car.id}/qr.png`}
              alt="Scan for price and details"
              className="h-36 w-36 mx-auto"
              data-testid="sticker-qr"
            />
            <p className="mt-1 text-xs font-bold uppercase tracking-wider" style={{ color: "#631724" }}>
              Scan for price, photos & details
            </p>
          </div>
        </div>

        {/* SPECS */}
        <div className="px-8 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-black/50">Vehicle Information</p>
          <div className="mt-3 grid grid-cols-2 gap-x-10 gap-y-2 text-sm" data-testid="sticker-specs">
            {specs.map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-black/10 py-1.5">
                <span className="text-black/55 uppercase text-xs tracking-wider pt-0.5">{k}</span>
                <span className="font-semibold font-mono text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FEATURES */}
        {car.features?.length > 0 && (
          <div className="px-8 pb-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-black/50">Features & Equipment</p>
            <div className="mt-3 grid grid-cols-3 gap-x-6 gap-y-1.5 text-sm" data-testid="sticker-features">
              {car.features.slice(0, 18).map((f) => (
                <span key={f} className="inline-flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#631724" }} /> {f}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* DESCRIPTION */}
        {car.description && (
          <div className="px-8 pb-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-black/50">About This Vehicle</p>
            <p className="mt-2 text-sm leading-relaxed text-black/80" data-testid="sticker-description">{car.description.split(/\n\s*(?:Highlights:|•)/)[0].trim()}</p>
          </div>
        )}

        {/* FOOTER */}
        <div className="mt-2 px-8 py-5 bg-black text-white">
          <div className="text-sm">
            <p className="font-serif text-xl">Come see it in person!</p>
            <p className="mt-1 text-white/80">{BRAND.addressLine1}, {BRAND.addressLine2}</p>
            <p className="text-white/80">{BRAND.phone} · {BRAND.email}</p>
            <p className="mt-2 text-xs tracking-[0.25em] uppercase" style={{ color: "#c9a227" }}>
              Family Owned · Honest Deals · {BRAND.state} Proud
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
